import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Globe, ChevronDown, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
];

const COOKIE_NAME = 'googtrans';

const readCookie = (name) => {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
};

const cookieDomains = () => {
  const host = window.location.hostname;
  if (!host || host === 'localhost') return [''];
  const parts = host.split('.');
  const domains = [host];
  if (parts.length >= 2) domains.push('.' + parts.slice(-2).join('.'));
  return domains;
};

const writeGoogTrans = (target) => {
  const value = `/en/${target}`;
  document.cookie = `${COOKIE_NAME}=${value};path=/`;
  cookieDomains().forEach((d) => {
    if (d) document.cookie = `${COOKIE_NAME}=${value};path=/;domain=${d}`;
  });
};

const clearGoogTrans = () => {
  const expire = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `${COOKIE_NAME}=;path=/;${expire}`;
  cookieDomains().forEach((d) => {
    if (d) document.cookie = `${COOKIE_NAME}=;path=/;domain=${d};${expire}`;
  });
};

const readCurrentLang = () => {
  const cookie = readCookie(COOKIE_NAME);
  if (!cookie) return 'en';
  const m = cookie.match(/^\/[a-z]{2,3}\/([a-z]{2,3})$/i);
  return m ? m[1] : 'en';
};

const applyTranslation = (target) => {
  const select = document.querySelector('.goog-te-combo');
  if (!select) return false;
  select.value = target === 'en' ? '' : target;
  select.dispatchEvent(new Event('change'));
  return true;
};

const LanguageSelector = () => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState('en');
  const wrapperRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setLang(readCurrentLang());
  }, []);

  // Re-apply translation after SPA route changes so freshly mounted text gets translated.
  useEffect(() => {
    const target = readCurrentLang();
    if (target === 'en') return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      if (applyTranslation(target) || attempts > 20) clearInterval(id);
    }, 150);
    return () => clearInterval(id);
  }, [location.pathname]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const change = useCallback(
    (code) => {
      setOpen(false);
      if (code === lang) return;
      setLang(code);
      if (code === 'en') {
        clearGoogTrans();
        // Reverting to source language reliably requires a reload.
        window.location.reload();
        return;
      }
      writeGoogTrans(code);
      // Try to translate immediately; if the widget hasn't initialized, fall back to reload
      // (the cookie ensures the new page comes up already translated).
      let attempts = 0;
      const tryApply = () => {
        if (applyTranslation(code)) return;
        attempts += 1;
        if (attempts > 20) {
          window.location.reload();
          return;
        }
        setTimeout(tryApply, 150);
      };
      tryApply();
    },
    [lang]
  );

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <div
      ref={wrapperRef}
      className="notranslate fixed right-5 bottom-5 2xl:bottom-auto 2xl:top-5 z-[9999]"
      translate="no"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Change language, current language ${current.label}`}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-[#febc04] rounded-xl shadow-md hover:shadow-lg hover:bg-[#FFFBEB] transition-all duration-200 font-bold text-[#123f1e] text-sm focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2"
      >
        <Globe className="w-4 h-4" aria-hidden="true" />
        <span>{current.label}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        // role="listbox" requires its DOM children to be role="option" (or role="group")
        // directly — wrapping each option in an <li> put a "listitem" role between them,
        // which axe's aria-required-children / aria-required-parent rules correctly flag.
        // A plain <div> (not <ul>) avoids re-introducing implicit list semantics ARIA doesn't
        // expect here.
        <div
          role="listbox"
          aria-label="Languages"
          className="absolute right-0 bottom-full mb-2 2xl:bottom-auto 2xl:top-full 2xl:mb-0 2xl:mt-2 min-w-[180px] bg-white border-2 border-[#febc04] rounded-xl shadow-xl overflow-hidden"
        >
          {LANGUAGES.map((option) => {
            const selected = lang === option.code;
            return (
              <button
                key={option.code}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => change(option.code)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-sm font-bold text-left transition-colors duration-150 focus:outline-none focus:bg-[#FFFBEB] ${
                  selected ? 'bg-[#FFFBEB] text-[#123f1e]' : 'text-[#123f1e] hover:bg-[#FFFBEB]'
                }`}
              >
                <span>{option.label}</span>
                {selected && <Check className="w-4 h-4 text-[#febc04]" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
